import { useCallback, useContext, useMemo } from 'react'

import getBufferETAFlag from '../../../../../../helpers/getBufferETAFlag'
import { useShoppingCartContext } from '../../../../providers/ShoppingCartProvider'
import { useLocationContext } from '../../../../../../contexts/location.context'
import { SearchContext } from '../../../../../common/context/SearchContextProvider'
import { LocalContext } from '../../../../../../helpers/useLocalContext'
import config from '../../../../../../../config/local-config'
import shoppingCartV2API from '../../../../api'
import { GTM } from '../../../../../../helpers'
import useExecuteGTM from './useExecuteGTM'
import nrLogger from '../../../../../../helpers/nrLogger'
import StopWatch from '../../../../logging/StopWatch'
import trackApplyCouponValidationInPL from '../../../../logging/gtm/trackApplyCouponValidationInPL'
import { useVehicleContext } from '../../../../../../contexts/vehicle.context'

const useAddToCartPL = params => {
  const {
    index,
    data,
    isNotAvailable,
    setNotify,
    setCartLoading,
    invObj,
    groundDeliveryTime,
    pageType,
    widgetName = '',
    executeEcomATCEvent
  } = params

  const {
    cartIdExists,
    getCartId,
    setCart,
    setCartId,
    getIdentifier,
  } = useShoppingCartContext()

  const locationState = useLocationContext()
  const storedZipCode = (locationState || {}).postalCode
  const storedState = (locationState || {}).state
  const storedCity = (locationState || {}).city
  const buffereta = Number(getBufferETAFlag())

  const vehicleState = useVehicleContext()
  const context = useContext(SearchContext)
  const { searchState = {} } = context || {}
  const { products = {} } = searchState
  const selectedVehicle = useMemo(() => {
    if (pageType === 'cart') {
      const vehicleDB = vehicleState?.vehicleDB || []
      return vehicleDB.find(element => element.selected) || null
    }
    return products?.selectedVehicle || {}
  }, [vehicleState, pageType, products])

  const { localContext } = useContext(LocalContext)
  const { history } = localContext

  const executeGTM = useExecuteGTM({
    index,
    pageType,
    data,
    selectedVehicle,
    invObj,
    groundDeliveryTime,
    widgetName,
    executeEcomATCEvent
  })

  return useCallback(
    async () => {
      if (isNotAvailable) {
        setNotify(true)
        return
      }

      try {
        setCartLoading(true)
        const item = {
          product: {
            productId: parseInt(data.id, 10),
            sku: data.sku,
            brand: {
              name: data.brand,
            },
          },
          modifiers: {
            quantity: 1,
          },
          referrer: __BROWSER__ ? window.location.href : '',
          buffereta,
        }

        if (selectedVehicle && selectedVehicle.year && selectedVehicle.make && selectedVehicle.model) {
          item.modifiers = {
            ...item.modifiers,
            vehicle: {
              details: {
                year: parseInt(selectedVehicle.year, 10),
                make: selectedVehicle.make,
                model: selectedVehicle.model,
                submodel: selectedVehicle.submodel || '',
                engine: selectedVehicle.engine || '',
              },
            },
          }
        }

        const items = [item]

        let res = {}
        const cartId = getCartId()
        const identifier = getIdentifier()

        const [baseZipCode] = (storedZipCode || '').split('-')
        const payload = {
          items,
          modifiers: {
            location: {
              zipcode: baseZipCode,
              state: storedState,
              city: storedCity,
            },
          },
          domain: config.hydra.site,
          buffereta,
        }

        const timer = new StopWatch()
        timer.start()

        if (cartIdExists() === false) {
          res = await shoppingCartV2API.items.create(identifier, payload)
          setCartId(res.cartId || '')
        } else {
          try {
            res = await shoppingCartV2API.items.add(identifier, cartId, items, buffereta)

            if (res && cartId !== res.cartId) {
              setCartId(res.cartId)
            }
          } catch (error1) {
            nrLogger.error('ShoppingCartV2.useAddToCartPL.error1', error1)
            if (error1.response && error1.response.status === 404) {
              res = await shoppingCartV2API.items.create(identifier, payload)
              setCartId(res.cartId || '')
            } else {
              throw error1
            }
          }
        }
        timer.stop()

        trackApplyCouponValidationInPL(res, timer.getElapsed())

        setCart(res)
        executeGTM()
        history.push('/cart')
      } catch (error2) {
        nrLogger.error('ShoppingCartV2.useAddToCartPL.error2', error2)
        setCartLoading(false)
        GTM.addToCartFail('SERP add to cart API call failed.')
        throw error2
      }
    },
    [
      cartIdExists,
      getCartId,
      setCart,
      setCartId,
      getIdentifier,
      data,
      isNotAvailable,
      setNotify,
      storedZipCode,
      storedState,
      storedCity,
      buffereta,
      setCartLoading,
      history,
      selectedVehicle,
      executeGTM,
    ],
  )
}

export default useAddToCartPL
